import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Search, Loader2, Pencil, X, GripVertical, LocateFixed, ChevronDown, ChevronUp } from "lucide-react";
import { titleCasePtBR } from "@/lib/textNormalize";

declare global {
  interface Window {
    google: any;
    initGoogleMaps?: () => void;
  }
}

export interface AddressData {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  /** Ponto de referência — só existe quando a loja habilita o campo (`fields`). */
  reference?: string;
  lat: number | null;
  lng: number | null;
  formattedAddress: string;
  /** "google" só no instante em que o GPS ou a busca preenchem o endereço.
   *  O checkout usa pra não deixar o ViaCEP trocar a rua que o Google deu:
   *  CEP de conjunto cobre várias quadras e a rua dele é genérica. */
  origin?: "google";
}

/** Configuração por loja de quais campos o cliente vê (stores.manual_address_fields). */
export type AddressFieldConfig = Partial<
  Record<
    "cep" | "neighborhood" | "city" | "state" | "street" | "number" | "complement" | "reference",
    { enabled: boolean; required: boolean }
  >
>;

interface AddressAutocompleteProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  googleMapsApiKey: string;
  showMap?: boolean;
  mapHeight?: number;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** Retornar false VETA a confirmação (ex.: checkout valida rua/número/bairro
   *  pra auto-salvar o 1º endereço antes de deixar confirmar). */
  onConfirmLocation?: () => boolean | void;
  compact?: boolean;
  manualMode?: boolean;
  onManualModeChange?: (v: boolean) => void;
  hideManualToggle?: boolean;
  /** Botão "Usar minha localização" (GPS → reverse geocode). Opt-in: só faz
   *  sentido quando quem digita É o destinatário (checkout público) — no PDV
   *  o GPS do operador seria a própria loja. O pin do GPS é a verdade física
   *  do cliente e elimina a loteria de geocoding de endereço escrito
   *  contraditório (incidente snackpetiscos 15/07/2026). */
  enableGeolocation?: boolean;
  /** O bairro vem de um select FORA deste componente (frete por bairro, onde a
   *  lista do lojista define a taxa). Impede QUALQUER escrita no bairro aqui —
   *  Google, ViaCEP ou digitação. */
  neighborhoodFromList?: boolean;
  /** Renderiza SÓ a busca (GPS + campo de pesquisa), sem o painel de campos,
   *  sem "Confirmar localização" e sem o card verde. Para quem já tem os campos
   *  de endereço na própria tela e quer a busca apenas como atalho que os
   *  preenche. A busca nasce ABERTA aqui: como não há nada a revelar depois
   *  dela, deixar colapsada só esconderia a única função do componente.
   *  ⚠️ Nasceu do incidente de 08/08/2026 — no modo normal a busca fica atrás
   *  de "ou buscar endereço manualmente" quando o GPS está ligado, e usar isso
   *  como formulário principal deixou 17 pedidos sem rua nem número. */
  searchOnly?: boolean;
  /** Campos que o submit do checkout apontou como obrigatórios não preenchidos:
   *  ganham destaque âmbar até serem preenchidos. */
  missingKeys?: string[];
  /** Abre já em modo de edição: sem o card verde de "confirmado" e com a busca
   *  expandida. Quem clica no lápis de um endereço SALVO quer editar agora —
   *  sem isto o componente monta confirmado e é preciso um SEGUNDO clique no
   *  "Editar" do card verde pra ver os campos. */
  openForEditing?: boolean;
  /** Campos configurados pela loja. Ausente = comportamento histórico (todos os
   *  campos do formulário, sem ponto de referência). Só o checkout público passa
   *  isto — PDV e endereço da própria loja seguem com o formulário completo. */
  fields?: AddressFieldConfig;
  /** Ao "Confirmar localização", se o pino andou mais de ~10 m desde o último
   *  endereço que veio do Google, reescreve pelo ponto novo (1 geocode por
   *  confirmação). Só troca o que ainda é do Google: rua e número digitados
   *  pelo cliente ficam; complemento e referência sempre ficam. Opção E,
   *  aprovada pelo Pedro em 12/10/2026 — só o checkout público liga. */
  rewriteOnConfirm?: boolean;
}

let googleMapsLoading = false;
let googleMapsLoaded = false;
const loadCallbacks: (() => void)[] = [];

// Prazo de validade do session token do Places. O Google encerra a sessão em
// ~3 min; 2,5 min dá margem para o round-trip da seleção acontecer com o token
// ainda vivo, sem girar antes da hora.
const SESSION_TOKEN_TTL_MS = 150_000;

type GoogleField = "street" | "number" | "neighborhood" | "city" | "state" | "zipCode";
// O que o Google escreveu por último. Campo que ainda bate com isso é "do
// Google" e pode ser reescrito; qualquer outra coisa o cliente digitou (no
// painel ou no modo manual) e fica. Sem caixa: o normalizador do blur troca
// maiúsculas e isso não é digitação.
type GoogleWritten = Partial<Record<GoogleField, string>>;
const sameText = (a?: string, b?: string) =>
  (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

const PIN_MOVED_METERS = 10;
const REWRITE_TIMEOUT_MS = 4_000;

function metersBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

// Melhor resultado do reverse geocode e a regra do número num lugar só — o
// GPS e a reescrita no confirmar usam a mesma.
function readReverseGeocode(results: any[]) {
  const best =
    (results || []).find((r: any) => r.types?.includes("street_address")) ||
    (results || [])[0];
  const parsed = best ? parseAddressComponents(best.address_components || []) : {};
  const formatted: string = best?.formatted_address || "";
  // Número só quando o Google aponta a casa em si (ROOFTOP). Nos resultados
  // interpolados/aproximados ele é chute — o cliente digita.
  const exato = best?.geometry?.location_type === "ROOFTOP";
  return {
    found: !!best,
    street: parsed.street || deriveStreetFromFormatted(formatted) || "",
    number: exato ? parsed.number || "" : "",
    neighborhood: parsed.neighborhood || "",
    city: parsed.city || "",
    state: parsed.state || "",
    zipCode: parsed.zipCode || "",
    formatted,
  };
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve) => {
    if (googleMapsLoaded && window.google?.maps) {
      resolve();
      return;
    }
    loadCallbacks.push(resolve);
    if (googleMapsLoading) return;
    googleMapsLoading = true;

    window.initGoogleMaps = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&callback=initGoogleMaps&language=pt-BR&region=BR&v=weekly`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });
}

function parseAddressComponents(components: any[]): Partial<AddressData> {
  const result: Partial<AddressData> = {};
  let premise = "";
  let subpremise = "";
  let neighborhoodFallback = "";
  for (const comp of components) {
    const types = comp.types as string[];
    const longName = comp.longText || comp.long_name || "";
    const shortName = comp.shortText || comp.short_name || "";
    if (types.includes("street_number")) {
      result.number = longName;
    } else if (types.includes("route")) {
      result.street = longName;
    } else if (types.includes("premise")) {
      premise = longName;
    } else if (types.includes("subpremise")) {
      subpremise = longName;
    } else if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
      result.neighborhood = longName;
    } else if (types.includes("sublocality_level_2") || types.includes("sublocality_level_3")) {
      if (!neighborhoodFallback) neighborhoodFallback = longName;
    } else if (types.includes("administrative_area_level_4") || types.includes("administrative_area_level_3")) {
      if (!neighborhoodFallback) neighborhoodFallback = longName;
    } else if (types.includes("locality")) {
      result.city = longName;
    } else if (types.includes("administrative_area_level_2")) {
      if (!result.city) result.city = longName;
    } else if (types.includes("administrative_area_level_1")) {
      result.state = shortName;
    } else if (types.includes("postal_code")) {
      result.zipCode = longName.replace(/\D/g, "");
    }
  }
  if (!result.neighborhood && neighborhoodFallback) {
    result.neighborhood = neighborhoodFallback;
  }
  if (!result.street && premise) {
    result.street = premise;
  }
  if (!result.number && subpremise) {
    result.number = subpremise;
  }
  return result;
}

function deriveStreetFromFormatted(formatted: string): string {
  if (!formatted) return "";
  const beforeDash = formatted.split(" - ")[0] ?? "";
  const beforeComma = beforeDash.split(",")[0] ?? "";
  return beforeComma.trim();
}

export function AddressAutocomplete({
  value,
  onChange: onChangeProp,
  googleMapsApiKey,
  showMap = true,
  mapHeight = 250,
  placeholder = "Digite seu endereço",
  className = "",
  inputClassName = "",
  onConfirmLocation,
  compact = false,
  manualMode: manualModeProp,
  onManualModeChange,
  hideManualToggle = false,
  enableGeolocation = false,
  openForEditing = false,
  fields,
  neighborhoodFromList = false,
  searchOnly = false,
  missingKeys,
  rewriteOnConfirm = false,
}: AddressAutocompleteProps) {
  // PONTO ÚNICO DE ESCRITA. Com `neighborhoodFromList`, o bairro vem de um
  // select FORA deste componente e é a autoridade da taxa de entrega: nem o
  // Google, nem o ViaCEP, nem digitação podem alterá-lo. São 21 call sites de
  // onChange aqui dentro — blindar um a um deixaria o próximo a esquecer, que
  // foi exatamente como o autofill de CEP zerou o frete de 99 lojas.
  const onChange = (next: AddressData) =>
    onChangeProp(
      neighborhoodFromList ? { ...next, neighborhood: value.neighborhood } : next,
    );
  // Rua, número e bairro NÃO respeitam o "desabilitado": são o endereço que o
  // motoboy usa, e ~100 lojas desligaram esses toggles enquanto a configuração
  // não tinha efeito nenhum aqui — esconder agora quebraria a entrega delas.
  // Contrato da config (08/08/2026): desligado = NÃO aparece; ligado = aparece;
  // ligado+obrigatório = aparece e barra o envio. Sem exceção de código: o
  // antigo alwaysOn de rua/nº/bairro virou DADO — as 152 lojas que tinham o
  // trio desligado da época em que o toggle era inerte foram normalizadas para
  // enabled (stores_maf_trio_backup_20260808), então desligado agora é sempre
  // escolha ativa do lojista.
  const shows = (key: keyof AddressFieldConfig): boolean => {
    // Com o bairro vindo do select da loja (frete por bairro), ele não some da
    // tela — mudou de lugar. Dois campos de bairro discordando é pior que um.
    if (key === "neighborhood" && neighborhoodFromList) return false;
    if (!fields) return key !== "reference";
    return fields[key]?.enabled === true;
  };
  const req = (key: keyof AddressFieldConfig): string =>
    fields?.[key]?.enabled && fields[key]?.required ? " *" : "";
  // Campo apontado como faltando pelo submit do checkout: destaque âmbar até
  // ser preenchido. O id addr-f-<campo> é o alvo do scroll+focus de lá.
  const missFlag = (key: keyof AddressFieldConfig, v: string | undefined | null): string =>
    missingKeys?.includes(key) && !(v && String(v).trim())
      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200 "
      : "";
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const [internalManualMode, setInternalManualMode] = useState(false);
  // Normaliza endereço PT-BR no blur dos 4 campos textuais (rua, bairro,
  // cidade, complemento). autoCapitalize="words" cuida da UX no teclado
  // mobile; o blur garante que desktop e preenchimento programático
  // tambem normalizem. NÃO usar em number, state, zipCode (formato rígido).
  const addressFieldProps = (field: "street" | "neighborhood" | "city" | "complement") => ({
    onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
      onChange({ ...value, [field]: titleCasePtBR(e.target.value) }),
    autoCapitalize: "words" as const,
  });
  const isManualControlled = manualModeProp !== undefined;
  const manualMode = isManualControlled ? manualModeProp! : internalManualMode;
  const setManualMode = (v: boolean) => {
    if (!isManualControlled) setInternalManualMode(v);
    onManualModeChange?.(v);
  };
  const [confirmed, setConfirmed] = useState(!openForEditing && !!value.lat && !!value.lng);
  const [cepLoading, setCepLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  // A busca do Google falhou (sem cobrança, rede, script que não carregou): antes a lista só ficava vazia e quem digitava
  // achava que «nada abre» (OHANA LANCHES, 02/11/2026, T-0044). Agora diz, com o caminho de preencher à mão.
  const [buscaFalhou, setBuscaFalhou] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const googleWrittenRef = useRef<GoogleWritten | null>(null);
  const lastGeoPointRef = useRef<{ lat: number; lng: number } | null>(null);
  const onConfirmLocationRef = useRef(onConfirmLocation);
  onConfirmLocationRef.current = onConfirmLocation;
  // Com GPS habilitado, a entrada manual (busca + campos) nasce CONTRAÍDA
  // atrás de "ou digitar endereço manualmente" — o caminho feliz é o botão de
  // localização. Expande no toggle ou automaticamente quando o GPS preenche.
  const [manualExpanded, setManualExpanded] = useState(!enableGeolocation || searchOnly || openForEditing);

  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const sessionTokenRef = useRef<any>(null);
  // Quando o token foi criado. Existe porque token do Places MORRE em ~3 min e
  // até 04/08/2026 nada aqui olhava a idade dele: era criado na 1ª tecla e só
  // descartado NA SELEÇÃO. Cliente que digitava, se distraía com outro campo e
  // voltava depois apresentava um token morto — aí o Google cobra cada tecla
  // como avulso E o Place Details não fecha a sessão.
  // Medido no billing: só 65% das seleções fechavam sessão
  // (Session Usage ÷ Place Details = 0,656, meta 0,85+), e a razão estava
  // estagnada havia 5 semanas mesmo depois do fix de token sob demanda (26/06).
  const sessionTokenAtRef = useRef(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!googleMapsApiKey) return;
    loadGoogleMapsScript(googleMapsApiKey).then(() => {
      setMapsReady(true);
    });
  }, [googleMapsApiKey]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) && inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowPredictions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initMap = useCallback((lat: number, lng: number) => {
    if (!mapRef.current || !window.google?.maps) return;
    const position = { lat, lng };

    const mapDiv = mapRef.current;
    const existingMap = mapInstanceRef.current;
    if (existingMap && existingMap.getDiv() === mapDiv) {
      existingMap.setCenter(position);
      if (markerRef.current) {
        markerRef.current.setPosition(position);
      }
      return;
    }

    mapInstanceRef.current = null;
    markerRef.current = null;

    const map = new window.google.maps.Map(mapDiv, {
      center: position,
      zoom: 16,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
    });
    mapInstanceRef.current = map;

    const marker = new window.google.maps.Marker({
      position,
      map,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
    });
    markerRef.current = marker;
    // Endereço salvo aberto pra editar não passou pelo Google aqui: o ponto de
    // partida do pino é a referência do "andou".
    if (!lastGeoPointRef.current) lastGeoPointRef.current = { lat, lng };

    marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      if (pos) {
        onChangeRef.current({ ...valueRef.current, lat: pos.lat(), lng: pos.lng() });
      }
    });
  }, []);

  useEffect(() => {
    if (showMap && mapsReady && value.lat && value.lng && !manualMode && !confirmed) {
      setTimeout(() => initMap(value.lat!, value.lng!), 150);
    }
  }, [showMap, mapsReady, value.lat, value.lng, manualMode, confirmed, initMap]);

  const searchPlaces = useCallback(async (input: string) => {
    if (input.length < 3) {
      setPredictions([]);
      setBuscaFalhou(false);
      return;
    }
    if (!window.google?.maps?.places) {
      setPredictions([]);
      setBuscaFalhou(true);
      return;
    }
    setLoading(true);
    // Cria o session token sob demanda, no início da busca — não no mount.
    // Token do Places expira em poucos minutos; criado no mount estaria velho
    // quando o cliente digita o endereço no checkout, e a sessão não fecharia
    // (autocompletes cobrados avulsos mesmo havendo seleção).
    //
    // E TROCA quando passa do prazo: token vencido não fecha sessão nenhuma, e
    // reusá-lo só garante que as próximas teclas TAMBÉM saiam avulsas. Girar
    // aqui não perde bundling — o que era da sessão velha já estava perdido no
    // momento em que ela venceu.
    const agora = Date.now();
    if (!sessionTokenRef.current || agora - sessionTokenAtRef.current > SESSION_TOKEN_TTL_MS) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      sessionTokenAtRef.current = agora;
    }
    try {
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ["br"],
        sessionToken: sessionTokenRef.current,
        language: "pt-BR",
      });
      setPredictions(suggestions || []);
      setShowPredictions((suggestions || []).length > 0);
      setBuscaFalhou(false);
    } catch {
      setPredictions([]);
      setBuscaFalhou(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handleInputChange = (val: string) => {
    setQuery(val);
    setConfirmed(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPlaces(val), 300);
  };

  const selectPrediction = async (suggestion: any) => {
    setShowPredictions(false);
    setLoading(true);

    try {
      const placePrediction = suggestion.placePrediction;
      if (!placePrediction) return;

      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "location", "formattedAddress"],
      });

      // Fecha a sessão: a próxima busca cria um token fresco (evita token expirado).
      sessionTokenRef.current = null;
      sessionTokenAtRef.current = 0;

      const components = place.addressComponents || [];
      const parsed = parseAddressComponents(components);

      const location = place.location;
      const lat = location?.lat() ?? null;
      const lng = location?.lng() ?? null;

      const formatted = place.formattedAddress || "";
      const streetFallback = parsed.street || deriveStreetFromFormatted(formatted);
      const newAddress: AddressData = {
        street: streetFallback || "",
        number: parsed.number || "",
        complement: "",
        neighborhood: parsed.neighborhood || "",
        city: parsed.city || "",
        state: parsed.state || "",
        zipCode: parsed.zipCode || "",
        lat,
        lng,
        formattedAddress: formatted,
        origin: "google",
      };

      // O número da busca é escolha do cliente (ele digitou na busca): fica de
      // fora do "do Google" e a reescrita no confirmar não o troca.
      googleWrittenRef.current = {
        street: newAddress.street, neighborhood: newAddress.neighborhood,
        city: newAddress.city, state: newAddress.state, zipCode: newAddress.zipCode,
      };
      lastGeoPointRef.current = lat != null && lng != null ? { lat, lng } : null;
      setQuery(place.formattedAddress || "");
      onChange(newAddress);
      setConfirmed(false);
    } catch (err) {
      console.error("Places API fetchFields error:", err);
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  };

  // GPS → reverse geocode → preenche o endereço e abre o mapa de confirmação.
  // O PIN salvo é o do GPS (verdade física do cliente), NÃO as coords do
  // resultado do geocode — o texto serve só pra leitura humana/motoboy; o
  // cliente ajusta o pin no mapa e completa número/complemento.
  const useMyLocation = () => {
    if (!navigator.geolocation || !window.google?.maps) return;
    setGeoError(null);
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          // Sem GPS de verdade (desktop via IP/Wi-Fi erra por km) o pin faria
          // mais mal que bem — orienta pra busca e não toca no endereço.
          if (accuracy > 100) {
            setGeoError("Sua localização está imprecisa por aqui — busque o endereço abaixo.");
            setManualExpanded(true);
            return;
          }
          const geocoder = new window.google.maps.Geocoder();
          const { results } = await geocoder.geocode({
            location: { lat: latitude, lng: longitude },
            language: "pt-BR",
          });
          const g = readReverseGeocode(results);
          googleWrittenRef.current = {
            street: g.street, number: g.number, neighborhood: g.neighborhood,
            city: g.city, state: g.state, zipCode: g.zipCode,
          };
          lastGeoPointRef.current = { lat: latitude, lng: longitude };
          onChange({
            street: g.street,
            number: g.number,
            complement: "",
            neighborhood: g.neighborhood,
            city: g.city,
            state: g.state,
            zipCode: g.zipCode,
            lat: latitude,
            lng: longitude,
            formattedAddress: g.formatted,
            origin: "google",
          });
          setQuery(g.formatted);
          setConfirmed(false);
          setManualExpanded(true);
        } catch {
          setGeoError("Não deu pra identificar o endereço — busque abaixo.");
          setManualExpanded(true);
        } finally {
          setGeoLoading(false);
        }
      },
      (erro) => {
        // Negado/indisponível (WebView do WhatsApp no iOS pode nem perguntar).
        // Expande a busca na hora — o cliente não fica num beco sem saída.
        // Negado (código 1) é o navegador: bloqueado antes, ou o Android não deixou perguntar por causa de um app por cima
        // da tela (o «Este site não pode pedir permissões», OHANA LANCHES, 02/11/2026). Tocar de novo não abre nada.
        setGeoLoading(false);
        setGeoError(
          erro?.code === 1
            ? "O navegador bloqueou a localização — busque o endereço abaixo."
            : "Sem acesso à sua localização — busque o endereço abaixo.",
        );
        setManualExpanded(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const finishConfirm = () => {
    if (onConfirmLocationRef.current?.() === false) return;
    setConfirmed(true);
  };

  // O pino andou desde o último endereço do Google → 1 geocode no ponto novo e
  // reescreve SÓ o que ainda é do Google. A validação do checkout roda depois,
  // sobre o texto novo (efeito abaixo): validar antes deixaria passar um
  // número que a reescrita limpou. Google falhou/demorou: confirma com o texto
  // que está — o ponto segue certo e vai no link do Maps.
  const handleConfirm = async () => {
    if (confirming) return;
    const base = lastGeoPointRef.current;
    const moved =
      rewriteOnConfirm &&
      !!base &&
      value.lat != null &&
      value.lng != null &&
      metersBetween(base, { lat: value.lat, lng: value.lng }) > PIN_MOVED_METERS;
    if (!moved || !window.google?.maps) {
      finishConfirm();
      return;
    }
    const point = { lat: value.lat!, lng: value.lng! };
    setConfirming(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const { results } = await Promise.race([
        geocoder.geocode({ location: point, language: "pt-BR" }),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), REWRITE_TIMEOUT_MS)),
      ]);
      const g = readReverseGeocode(results);
      if (!g.found) {
        finishConfirm();
        return;
      }
      const prev = googleWrittenRef.current ?? {};
      const doGoogle = (f: GoogleField) => !value[f] || sameText(value[f], prev[f]);
      const next: AddressData = { ...value };
      const written: GoogleWritten = {};
      for (const f of ["street", "neighborhood", "city", "state", "zipCode"] as const) {
        if (!doGoogle(f)) continue;
        if (g[f]) next[f] = g[f];
        written[f] = next[f];
      }
      // O número automático era da casa anterior: troca pelo do ponto novo
      // (só ROOFTOP) ou limpa pro cliente digitar. Digitado pelo cliente fica.
      if (doGoogle("number")) {
        next.number = g.number;
        written.number = g.number;
      }
      googleWrittenRef.current = written;
      lastGeoPointRef.current = point;
      onChange({ ...next, formattedAddress: g.formatted, origin: "google" });
      setConfirmPending(true);
    } catch {
      finishConfirm();
    } finally {
      setConfirming(false);
    }
  };

  // Roda no render seguinte ao da reescrita: aí o checkout já tem o texto novo
  // e a validação dele (onConfirmLocation) olha o que vai ser enviado.
  useEffect(() => {
    if (!confirmPending) return;
    setConfirmPending(false);
    finishConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmPending]);

  const lookupCep = async (cep: string) => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await resp.json();
      if (!data.erro) {
        onChange({
          ...value,
          zipCode: clean,
          street: data.logradouro || value.street,
          neighborhood: data.bairro || value.neighborhood,
          city: data.localidade || value.city,
          state: data.uf || value.state,
        });
      }
    } catch {} finally {
      setCepLoading(false);
    }
  };

  if (manualMode) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-1 text-sm font-semibold"><MapPin className="w-4 h-4" /> Endereço</Label>
          {googleMapsApiKey && mapsReady && (
            <button type="button" onClick={() => setManualMode(false)} className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
              <Search className="w-3 h-3" /> Buscar endereço
            </button>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3">
          {shows("cep") && (
            <div className="col-span-2 space-y-1">
              <Label className="text-xs text-muted-foreground">CEP{req("cep")}</Label>
              <div className="relative">
                <Input
                  value={(value.zipCode || "").replace(/^(\d{5})(\d{1,3})$/, "$1-$2")}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                    onChange({ ...value, zipCode: digits });
                    if (digits.length === 8) lookupCep(digits);
                  }}
                  placeholder="00000-000"
                  maxLength={10}
                  id="addr-f-cep"
                  className={`${missFlag("cep", value.zipCode)}bg-muted/30 ${inputClassName}`}
                />
                {cepLoading && <Loader2 className="w-3 h-3 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
              </div>
            </div>
          )}
          {shows("city") && (
            <div className="col-span-2 space-y-1">
              <Label className="text-xs text-muted-foreground">Cidade{req("city")}</Label>
              <Input id="addr-f-city" value={value.city || ""} onChange={(e) => onChange({ ...value, city: e.target.value })} {...addressFieldProps("city")} className={`${missFlag("city", value.city)}bg-muted/30 ${inputClassName}`} />
            </div>
          )}
          {shows("street") && (
            <div className="col-span-3 space-y-1">
              <Label className="text-xs text-muted-foreground">Rua / Avenida{req("street")}</Label>
              <Input id="addr-f-street" value={value.street || ""} onChange={(e) => onChange({ ...value, street: e.target.value })} {...addressFieldProps("street")} className={`${missFlag("street", value.street)}bg-muted/30 ${inputClassName}`} />
            </div>
          )}
          {shows("number") && (
            <div className="col-span-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Nº{req("number")}</Label>
              <Input id="addr-f-number" value={value.number || ""} onChange={(e) => onChange({ ...value, number: e.target.value })} className={`${missFlag("number", value.number)}bg-muted/30 ${inputClassName}`} />
            </div>
          )}
          {shows("neighborhood") && (
            <div className="col-span-2 space-y-1">
              <Label className="text-xs text-muted-foreground">Bairro{req("neighborhood")}</Label>
              <Input id="addr-f-neighborhood" value={value.neighborhood || ""} onChange={(e) => onChange({ ...value, neighborhood: e.target.value })} {...addressFieldProps("neighborhood")} className={`${missFlag("neighborhood", value.neighborhood)}bg-muted/30 ${inputClassName}`} />
            </div>
          )}
          {shows("state") && (
            <div className="col-span-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Estado{req("state")}</Label>
              <Input id="addr-f-state" value={value.state || ""} onChange={(e) => onChange({ ...value, state: e.target.value })} className={`${missFlag("state", value.state)}bg-muted/30 ${inputClassName}`} maxLength={2} />
            </div>
          )}
          {shows("complement") && (
            <div className="col-span-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Complemento{req("complement")}</Label>
              <Input id="addr-f-complement" value={value.complement || ""} onChange={(e) => onChange({ ...value, complement: e.target.value })} {...addressFieldProps("complement")} className={`${missFlag("complement", value.complement)}bg-muted/30 ${inputClassName}`} placeholder={fields?.complement?.required ? undefined : "Opcional"} />
            </div>
          )}
          {shows("reference") && (
            <div className="col-span-4 space-y-1">
              <Label className="text-xs text-muted-foreground">Ponto de referência{req("reference")}</Label>
              <Input value={value.reference || ""} onChange={(e) => onChange({ ...value, reference: e.target.value })} className={`bg-muted/30 ${inputClassName}`} placeholder="Próximo a..." />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {enableGeolocation && mapsReady && typeof navigator !== "undefined" && "geolocation" in navigator && (
        <div className="space-y-1">
          <button
            type="button"
            onClick={useMyLocation}
            disabled={geoLoading}
            className="w-full inline-flex items-center justify-center gap-2 h-11 px-3 rounded-xl border border-primary/40 bg-primary/5 hover:bg-primary/10 text-base font-medium text-primary transition-colors disabled:opacity-60"
          >
            {geoLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LocateFixed className="w-5 h-5" />}
            {geoLoading ? "Localizando..." : "Usar minha localização atual"}
          </button>
          {geoError && <p className="text-xs text-muted-foreground text-center">{geoError}</p>}
        </div>
      )}

      {enableGeolocation && !searchOnly && (
        <button
          type="button"
          onClick={() => setManualExpanded((v) => !v)}
          className="w-full flex items-center justify-center gap-1 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ou buscar endereço manualmente
          {manualExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      )}

      {manualExpanded && (
      <div className="relative">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => { if (predictions.length > 0) setShowPredictions(true); }}
            placeholder={placeholder}
            className={`pl-9 pr-8 ${inputClassName}`}
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
          {query && !loading && (
            <button type="button" onClick={() => { setQuery(""); setPredictions([]); setShowPredictions(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {showPredictions && predictions.length > 0 && (
          <div ref={dropdownRef} className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden max-h-60 overflow-y-auto">
            {predictions.map((suggestion, idx) => {
              const pred = suggestion.placePrediction;
              if (!pred) return null;
              return (
                <button
                  key={pred.placeId || idx}
                  type="button"
                  onClick={() => selectPrediction(suggestion)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0 cursor-pointer"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{pred.mainText?.text || pred.text?.text || ""}</p>
                      <p className="text-xs text-muted-foreground">{pred.secondaryText?.text || ""}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {buscaFalhou && (
          <p className="mt-1.5 text-xs text-destructive" data-testid="endereco-busca-falhou">
            A busca de endereço não respondeu.
            {!searchOnly && (
              <>
                {" "}
                <button type="button" onClick={() => setManualMode(true)} className="font-medium underline underline-offset-2" data-testid="endereco-busca-falhou-manual">
                  Preencher o endereço à mão
                </button>
              </>
            )}
          </p>
        )}

        {enableGeolocation && !searchOnly && !buscaFalhou && (
          <button
            type="button"
            onClick={() => setManualMode(true)}
            className="mt-2 block ml-auto text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            busca não está funcionando
          </button>
        )}
      </div>
      )}

      {!searchOnly && manualExpanded && value.lat && value.lng && !confirmed && (
        <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="grid grid-cols-4 gap-2">
            {shows("street") && (
              <div className="col-span-3">
                <Input id="addr-f-street" value={value.street || ""} onChange={(e) => onChange({ ...value, street: e.target.value })} {...addressFieldProps("street")} placeholder={`Rua${req("street")}`} className={`${missFlag("street", value.street)}text-sm bg-muted/30 ${inputClassName}`} />
              </div>
            )}
            {shows("number") && (
              <div className="col-span-1">
                <Input id="addr-f-number" value={value.number || ""} onChange={(e) => onChange({ ...value, number: e.target.value })} placeholder={`Nº${req("number")}`} className={`${missFlag("number", value.number)}text-sm bg-muted/30 ${inputClassName}`} />
              </div>
            )}
            {shows("complement") && (
              <div className="col-span-2">
                <Input id="addr-f-complement" value={value.complement || ""} onChange={(e) => onChange({ ...value, complement: e.target.value })} {...addressFieldProps("complement")} placeholder={`Complemento${req("complement")}`} className={`${missFlag("complement", value.complement)}text-sm bg-muted/30 ${inputClassName}`} />
              </div>
            )}
            {shows("neighborhood") && (
              <div className="col-span-2">
                <Input id="addr-f-neighborhood" value={value.neighborhood || ""} onChange={(e) => onChange({ ...value, neighborhood: e.target.value })} {...addressFieldProps("neighborhood")} placeholder={`Bairro${req("neighborhood")}`} className={`${missFlag("neighborhood", value.neighborhood)}text-sm bg-muted/30 ${inputClassName}`} />
              </div>
            )}
            {/* Com config salva a config MANDA, inclusive no compacto (ligado =
                aparece; era o "obrigatório sem campo na tela" do painel
                compacto). Sem config, compacto segue enxuto como sempre. */}
            {(fields ? true : !compact) && (
              <>
                {shows("city") && (
                  <div className="col-span-2">
                    <Input id="addr-f-city" value={value.city || ""} onChange={(e) => onChange({ ...value, city: e.target.value })} {...addressFieldProps("city")} placeholder={`Cidade${req("city")}`} className={`${missFlag("city", value.city)}text-sm bg-muted/30 ${inputClassName}`} />
                  </div>
                )}
                {shows("state") && (
                  <div className="col-span-1">
                    <Input id="addr-f-state" value={value.state || ""} onChange={(e) => onChange({ ...value, state: e.target.value })} placeholder={`UF${req("state")}`} className={`${missFlag("state", value.state)}text-sm bg-muted/30 ${inputClassName}`} maxLength={2} />
                  </div>
                )}
                {shows("cep") && (
                  // CEP tem tamanho fixo (99999-999): a CÉLULA fica com 2
                  // colunas pra nunca cortar em tela estreita, mas o campo em
                  // si é `w-28` — o suficiente pros 9 caracteres com folga, em
                  // vez de esticar por meia linha.
                  <div className="col-span-2">
                    <Input id="addr-f-cep" value={(value.zipCode || "").replace(/^(\d{5})(\d{1,3})$/, "$1-$2")} onChange={(e) => onChange({ ...value, zipCode: e.target.value.replace(/\D/g, "") })} placeholder={`CEP${req("cep")}`} maxLength={9} inputMode="numeric" className={`${missFlag("cep", value.zipCode)}text-sm bg-muted/30 w-28 ${inputClassName}`} />
                  </div>
                )}
              </>
            )}
            {shows("reference") && (
              <div className="col-span-4">
                <Input id="addr-f-reference" value={value.reference || ""} onChange={(e) => onChange({ ...value, reference: e.target.value })} placeholder={`Ponto de referência (próximo a...)${req("reference")}`} className={`${missFlag("reference", value.reference)}text-sm bg-muted/30 ${inputClassName}`} />
              </div>
            )}
          </div>

          {showMap && (
            <div className="rounded-xl overflow-hidden border border-border shadow-sm">
              <div ref={mapRef} style={{ width: "100%", height: `${mapHeight}px` }} />
              <div className="bg-muted/30 px-3 py-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <GripVertical className="w-3 h-3" />
                Arraste o pin para ajustar a localização exata
              </div>
            </div>
          )}

          <Button type="button" onClick={handleConfirm} disabled={confirming} className="w-full" size={compact ? "sm" : "default"}>
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />} Confirmar localização
          </Button>
        </div>
      )}

      {!searchOnly && confirmed && value.lat && value.lng && (
        // Cores do TEMA da loja, não verde fixo: este card fica ao lado do
        // "Usar minha localização atual" e dos cards de endereço salvo, que já
        // usam accent/primary — o verde era a única peça fora do padrão.
        <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-accent bg-accent/10 text-sm text-foreground">
          <MapPin className="w-4 h-4 shrink-0 text-accent" />
          <span className="flex-1 truncate">{value.formattedAddress || `${value.street}${value.number ? `, ${value.number}` : ''} — ${value.neighborhood || value.city}`}</span>
          {/* `setManualExpanded(true)` é o que faz o lápis abrir alguma coisa:
              com geolocalização ligada a busca nasce FECHADA, então tirar o
              confirmado sumia com o card verde e não mostrava formulário
              nenhum — parecia que só dava pra editar clicando de novo em
              "Usar minha localização atual". */}
          <button
            type="button"
            onClick={() => {
              setConfirmed(false);
              setManualExpanded(true);
              setQuery(value.formattedAddress || "");
            }}
            className="inline-flex items-center gap-1 text-accent hover:text-accent/80 font-medium shrink-0"
          >
            <Pencil className="w-4 h-4" />
            Editar
          </button>
        </div>
      )}

      {!hideManualToggle && (
        <button
          type="button"
          onClick={() => setManualMode(true)}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-background hover:bg-muted text-sm font-medium text-foreground transition-colors self-start"
        >
          <Pencil className="w-4 h-4" />
          Digitar endereço manualmente
        </button>
      )}
    </div>
  );
}
